// ─────────────────────────────────────────────────────────────────────────────
//  services/queries.js — Secure reads via SECURITY DEFINER query functions
//
//  The RLS model (020) grants direct SELECT on only 5 tables. All business/
//  financial data is read through SECURITY DEFINER functions that check active
//  membership. Each function returns a JSONB array.
//
//  Calls go through PostgREST RPC: POST /rest/v1/rpc/{fn}
//  Public-schema wrappers delegate to queries.* (patch 003).
// ─────────────────────────────────────────────────────────────────────────────
import { config } from '../platform/config';
import { AppError, ERRORS } from '../platform/errors';

const RPC_URL = (fn) => `${config.SUPABASE_URL}/rest/v1/rpc/${fn}`;

const fireSessionExpired = () => {
  try { window.dispatchEvent(new CustomEvent('faida:session-expired')); } catch {}
};

// Internal RPC caller for query functions (public-schema wrappers → queries.*)
async function queryRpc(fnName, params, token) {
  if (!token) { fireSessionExpired(); throw new AppError('ERR_PERMISSION', 'Not authenticated'); }

  const headers = {
    'Content-Type': 'application/json',
    apikey: config.SUPABASE_ANON_KEY,
    Authorization: `Bearer ${token}`,
  };

  let res;
  try {
    res = await fetch(RPC_URL(fnName), {
      method: 'POST',
      headers,
      body: JSON.stringify(params || {}),
    });
  } catch (err) {
    throw new AppError('ERR_INTERNAL', `Network error: ${err.message}`);
  }

  if (res.status === 401) { fireSessionExpired(); throw new AppError('ERR_PERMISSION', 'Session expired'); }

  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const msg = body?.message || '';
    if (msg.includes('ERR_PERMISSION')) throw new AppError('ERR_PERMISSION');
    throw new AppError('ERR_INTERNAL', msg || `Query failed: ${res.status}`);
  }

  return body; // JSONB array or object returned by the function
}

// ═══════════════════════ MEMBERSHIP / BUSINESS ═══════════════════════════════

export async function fetchMyBusinesses(token) {
  return queryRpc('my_memberships', {}, token);
}

export async function fetchProfile(token) {
  return queryRpc('my_profile', {}, token);
}

export async function fetchTeamMembers(businessId, token) {
  return queryRpc('team_members', { p_business_id: businessId }, token);
}

export async function fetchAvailableRoles(token) {
  return queryRpc('available_roles', {}, token);
}

export async function fetchPermissionMatrix(businessId, token) {
  return queryRpc('permission_matrix', { p_business_id: businessId }, token);
}

// ── Edge Function: send invite email (best-effort, never throws) ────────────
export async function sendInviteEmail({ email, businessName, inviterName, inviteCode, roleName }, token) {
  try {
    const res = await fetch(`${config.SUPABASE_URL}/functions/v1/send-invite-email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        apikey: config.SUPABASE_ANON_KEY,
      },
      body: JSON.stringify({ email, businessName, inviterName, inviteCode, roleName }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      console.warn('[INVITE-EMAIL] Failed:', body?.error || res.status);
      return { sent: false, error: body?.error || 'Email delivery failed' };
    }
    return { sent: true, emailId: body.emailId };
  } catch (err) {
    console.warn('[INVITE-EMAIL] Network error:', err.message);
    return { sent: false, error: err.message };
  }
}

// ═══════════════════════ MASTER DATA ═════════════════════════════════════════

export async function fetchProducts(businessId, token) {
  return queryRpc('products', { p_business_id: businessId }, token);
}

export async function fetchCustomers(businessId, token) {
  return queryRpc('customers', { p_business_id: businessId }, token);
}

export async function fetchSuppliers(businessId, token) {
  return queryRpc('suppliers', { p_business_id: businessId }, token);
}

export async function fetchExpenseCategories(businessId, token) {
  return queryRpc('expense_categories', { p_business_id: businessId }, token);
}

export async function fetchPaymentAccounts(businessId, token) {
  return queryRpc('payment_accounts', { p_business_id: businessId }, token);
}

export async function fetchServices(businessId, token) {
  return queryRpc('services', { p_business_id: businessId }, token);
}

// ═══════════════════════ TRANSACTIONS ════════════════════════════════════════

export async function fetchSales(businessId, token) {
  return queryRpc('sales', { p_business_id: businessId }, token);
}

export async function fetchPurchases(businessId, token) {
  return queryRpc('purchases', { p_business_id: businessId }, token);
}

export async function fetchExpenses(businessId, token) {
  return queryRpc('expenses', { p_business_id: businessId }, token);
}

export async function fetchServiceTransactions(businessId, token) {
  return queryRpc('service_transactions', { p_business_id: businessId }, token);
}

export async function fetchSalesReturns(businessId, token) {
  return queryRpc('sales_returns', { p_business_id: businessId }, token);
}

export async function fetchPurchaseReturns(businessId, token) {
  return queryRpc('purchase_returns', { p_business_id: businessId }, token);
}

export async function fetchInvoices(businessId, token) {
  return queryRpc('invoices', { p_business_id: businessId }, token);
}

// ═══════════════════════ WALLET / CASH ═══════════════════════════════════════

export async function fetchCashAdjustments(businessId, token) {
  return queryRpc('cash_adjustments', { p_business_id: businessId }, token);
}

export async function fetchWalletTransfers(businessId, token) {
  return queryRpc('wallet_transfers', { p_business_id: businessId }, token);
}

// ═══════════════════════ INVENTORY ═══════════════════════════════════════════

export async function fetchInventoryMovements(businessId, token, productId) {
  return queryRpc('inventory_movements', {
    p_business_id: businessId,
    p_product_id: productId || null,
  }, token);
}

// ═══════════════════════ BULK LOAD ═══════════════════════════════════════════

// Single RPC call that returns all business data as a keyed JSON object.
// More efficient than 15 parallel calls.
export async function fetchAllBusinessData(businessId, token) {
  return queryRpc('all_business_data', { p_business_id: businessId }, token);
}
